import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Volume2, BookOpen, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { heroImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { getSlangOfTheDay, getRandomSlang } from '../services/api';

const SlangSection = () => {
  const [currentWord, setCurrentWord] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { language, t } = useLanguage();

  const loadSlangOfTheDay = useCallback(async () => {
    try {
      const word = await getSlangOfTheDay();
      setCurrentWord(word);
    } catch (err) {
      // Silently fail - will show loading state
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlangOfTheDay();
  }, [loadSlangOfTheDay]);

  const getNewWord = async () => {
    setIsFlipping(true);
    try {
      const word = await getRandomSlang();
      setTimeout(() => {
        setCurrentWord(word);
        setIsFlipping(false);
      }, 300);
    } catch (err) {
      // Silently fail - keep current word
      setIsFlipping(false);
    }
  };

  // Helper function to get translated content
  const getTranslated = (field) => {
    if (!currentWord) return '';
    if (language === 'es' && currentWord[`${field}Es`]) {
      return currentWord[`${field}Es`];
    }
    if (language === 'fr' && currentWord[`${field}Fr`]) {
      return currentWord[`${field}Fr`];
    }
    return currentWord[field];
  };

  if (isLoading) {
    return (
      <section className="py-24 bg-gradient-to-br from-[#000080] via-[#000060] to-[#000040] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-white/20 rounded-lg w-64 mx-auto mb-4"></div>
            <div className="h-8 bg-white/20 rounded-lg w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!currentWord) return null;

  return (
    <section id="slang" className="py-24 bg-gradient-to-br from-[#000080] via-[#000060] to-[#000040] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 opacity-10">
        <img
          src={heroImages.bougainvillea}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute bottom-0 right-0 w-80 h-80 opacity-10">
        <img
          src={heroImages.hibiscus}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#FFFF00] text-[#000080] rounded-full px-4 py-2 mb-6 shadow-lg">
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold text-sm">{t('slang.badge')}</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            {t('slang.title')}
          </h2>
          <p className="text-white/70 text-lg">
            {t('slang.subtitle')}
          </p>
        </div>

        {/* Slang card */}
        <div
          className={`slang-card bg-white rounded-3xl p-8 md:p-12 shadow-2xl transition-transform duration-300 ${
            isFlipping ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
          }`}
        >
          <div className="flex flex-col md:flex-row gap-8">
            {/* Word section */}
            <div className="flex-1">
              <div className="mb-6">
                <h3 className="font-display text-4xl md:text-5xl font-bold text-[#000080] mb-2">
                  {currentWord.word}
                </h3>
                <div className="flex items-center gap-2 text-gray-500">
                  <Volume2 className="w-4 h-4" />
                  <span className="italic">/{currentWord.pronunciation}/</span>
                </div>
              </div>

              <div className="bg-[#8B5CF6]/20 rounded-xl p-4 mb-6 border border-[#8B5CF6]/30">
                <p className="text-[#000080] font-bold text-lg">
                  {getTranslated('meaning')}
                </p>
              </div>

              <div className="mb-6">
                <p className="text-gray-500 text-sm mb-1">{t('slang.example')}</p>
                <p className="text-gray-700 italic">"{getTranslated('example')}"</p>
              </div>

              <div className="flex items-start gap-3 bg-[#22C55E]/10 rounded-xl p-4">
                <BookOpen className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-500 text-xs mb-1">{t('slang.funFact')}</p>
                  <p className="text-gray-700 text-sm">{getTranslated('funFact')}</p>
                </div>
              </div>
            </div>

            {/* Action section */}
            <div className="flex flex-col justify-center items-center md:items-end gap-4">
              <Button
                onClick={getNewWord}
                disabled={isFlipping}
                className="btn-tropical bg-[#FF6B35] hover:bg-[#E85A2A] text-white font-semibold px-6 py-6 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${isFlipping ? 'animate-spin' : ''}`} />
                {t('slang.newWord')}
              </Button>
              <p className="text-gray-400 text-sm text-center md:text-right whitespace-pre-line">
                {t('slang.comeBack')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SlangSection;
